import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { 
  Building2, Home, Heart, Brain, Stethoscope, Pill, Hospital, Package,
  Users, FileText, Calendar, DollarSign, ClipboardList,
  Phone, Mail, Shield, Activity, CheckCircle, Zap, Target, Globe, HeartHandshake, AlertTriangle
} from 'lucide-react';

// Service Navigation HIPAA Modal for Quick Links
const ServiceHIPAAModal = ({ isOpen, onAccept, onDecline, targetService }) => {
  const [acknowledged, setAcknowledged] = useState(false);
  
  React.useEffect(() => {
    if (isOpen) setAcknowledged(false);
  }, [isOpen]);
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[100]">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full mx-4 overflow-hidden">
        <div className="bg-gradient-to-r from-orange-500 to-orange-600 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
              <Shield className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-white">Service Access Authorization</h3>
              <p className="text-orange-100 text-sm">HIPAA Compliance Required</p>
            </div>
          </div>
        </div>
        
        <div className="p-6">
          <div className="flex items-start gap-3 mb-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
            <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800">
              You are about to access <strong>{targetService}</strong>. This area may contain Protected Health Information (PHI).
            </p>
          </div>
          
          <ul className="text-sm text-gray-600 space-y-2 mb-4">
            <li className="flex items-start gap-2">
              <span className="text-orange-500 mt-1">•</span>
              You will only access information necessary for your job duties
            </li>
            <li className="flex items-start gap-2">
              <span className="text-orange-500 mt-1">•</span>
              All access is logged and audited for compliance
            </li>
          </ul>
          
          <label className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100">
            <input 
              type="checkbox" 
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="w-4 h-4 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
            />
            <span className="text-sm text-gray-700 font-medium">I agree to comply with HIPAA regulations</span>
          </label>
        </div>
        
        <div className="px-6 py-4 bg-gray-50 border-t flex justify-end gap-3">
          <button onClick={onDecline} className="px-4 py-2 text-gray-600 hover:bg-gray-200 rounded-lg text-sm font-medium">Cancel</button>
          <button 
            onClick={onAccept}
            disabled={!acknowledged}
            className={`px-4 py-2 rounded-lg text-sm font-medium ${acknowledged ? 'bg-orange-500 text-white hover:bg-orange-600' : 'bg-gray-300 text-gray-500 cursor-not-allowed'}`}
          >
            Proceed to {targetService}
          </button>
        </div>
      </div>
    </div>
  );
};

const QuickLinksPage = () => {
  const navigate = useNavigate();
  const [hipaaModal, setHipaaModal] = useState({ open: false, target: '', href: '' });

  const handleServiceClick = (e, serviceName, href) => {
    e.preventDefault();
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/login');
      return;
    }
    setHipaaModal({ open: true, target: serviceName, href });
  };

  const quickLinkCategories = [
    {
      name: 'Home Health',
      icon: Home,
      color: 'bg-green-500',
      href: '/dashboard'
    },
    {
      name: 'Home Care',
      icon: Users,
      color: 'bg-purple-500',
      href: '/dashboard/patients'
    },
    {
      name: 'Hospice',
      icon: Heart,
      color: 'bg-blue-500',
      href: '/dashboard/clinical'
    },
    {
      name: 'Clinic',
      icon: Building2,
      color: 'bg-teal-500',
      href: '/dashboard/clinic'
    },
    {
      name: 'Behavioral Health',
      icon: Brain,
      color: 'bg-orange-500',
      href: '/dashboard/behavioral-health'
    },
    {
      name: 'DME',
      icon: Pill,
      color: 'bg-amber-500',
      href: '/dashboard/billing'
    },
    {
      name: 'Case Management',
      icon: Shield,
      color: 'bg-red-500',
      href: '/dashboard/patients'
    },
    {
      name: 'Concierge Visiting Doctors',
      icon: Stethoscope,
      color: 'bg-purple-600',
      href: '/concierge'
    }
  ];

  // Service buttons with routes (replacing core values)
  const serviceButtons = [
    { icon: Home, title: 'Home Health', color: 'bg-green-500', href: '/dashboard/services/home-health' },
    { icon: Users, title: 'Home Care', color: 'bg-purple-500', href: '/dashboard/services/home-care' },
    { icon: Heart, title: 'Hospice', color: 'bg-blue-500', href: '/dashboard/services/hospice' },
    { icon: Hospital, title: 'Clinic', color: 'bg-teal-500', href: '/dashboard/services/clinic' },
    { icon: Brain, title: 'Behavioral Health', color: 'bg-orange-500', href: '/dashboard/services/behavioral-health' },
    { icon: Package, title: 'DME', color: 'bg-yellow-500', href: '/dashboard/services/dme' },
    { icon: ClipboardList, title: 'Case Management', color: 'bg-red-500', href: '/dashboard/services/case-management' },
    { icon: Stethoscope, title: 'Concierge Visiting Doctors', color: 'bg-purple-600', href: '/dashboard/services/concierge' },
  ];

  const mainLinks = [
    { name: 'Patient Demographics', href: '/dashboard/patients/demographics', icon: Users },
    { name: 'Scheduling', href: '/dashboard/scheduling', icon: Calendar },
    { name: 'Clinical Forms', href: '/dashboard/forms/oasis', icon: ClipboardList },
    { name: 'Billing', href: '/dashboard/billing', icon: DollarSign },
    { name: 'OASIS Assessment', href: '/dashboard/forms/oasis', icon: FileText },
    { name: 'Plan of Care (485)', href: '/dashboard/clinical/poc-485', icon: Stethoscope },
    { name: 'IvoryMail', href: '/dashboard/ivorymail', icon: Mail },
    { name: 'HIPAA Compliance', href: '/dashboard/hipaa', icon: Shield },
  ];

  return (
    <DashboardLayout>
      <div className="min-h-screen -m-6">
        {/* Hero Section */}
        <div className="relative bg-gradient-to-br from-orange-500 to-orange-600 overflow-hidden">
          {/* Background Pattern */}
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
            }} />
          </div>
          
          <div className="relative max-w-7xl mx-auto px-6 py-12 lg:py-16">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              {/* Left Content */}
              <div className="text-white">
                <h1 className="text-3xl lg:text-4xl font-bold leading-tight mb-8">
                  IVORY MEDICAL HOME IS an all-in-one AI-enabled EMR + management system{' '}
                  <span className="text-green-300">for a Medical Home model of care.</span>
                </h1>
                <p className="text-base text-orange-100 mb-8">
                  It is an All-in-one operational intelligence Platform built to function as an in-house, ecosystems, engineered to unify and streamline the full continuum of services after hospitalization.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link 
                    to="/dashboard" 
                    className="px-6 py-3 bg-white text-orange-600 rounded-lg font-semibold hover:bg-orange-50 transition-colors"
                  >
                    Go to Dashboard
                  </Link>
                  <Link 
                    to="/dashboard/document-generator" 
                    className="px-6 py-3 bg-orange-700 text-white rounded-lg font-semibold hover:bg-orange-800 transition-colors"
                  >
                    AI Document Generator
                  </Link>
                </div>
              </div>
              
              {/* Right Image */}
              <div className="hidden lg:block">
                <div className="relative">
                  <div className="absolute -inset-4 bg-white/10 rounded-3xl blur-2xl"></div>
                  <img 
                    src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=600&h=400&fit=crop"
                    alt="Healthcare Professional"
                    className="relative rounded-2xl shadow-2xl w-full h-auto object-cover"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* HIPAA Modal */}
          <ServiceHIPAAModal
            isOpen={hipaaModal.open}
            onAccept={() => { setHipaaModal({ open: false, target: '', href: '' }); navigate(hipaaModal.href); }}
            onDecline={() => setHipaaModal({ open: false, target: '', href: '' })}
            targetService={hipaaModal.target}
          />
        </div>

        {/* Core Identity Section */}
        <div className="bg-gray-900 text-white py-12 px-6">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl font-bold mb-6 text-center">
              The Operational Backbone of Modern Healthcare
            </h2>
            <p className="text-gray-300 text-center max-w-4xl mx-auto mb-8">
              The system delivers smart automation, clinical intelligence, operational oversight, and seamless coordination across all service lines. By embedding AI into documentation, scheduling, care planning, compliance, billing, communication, and quality monitoring, Ivory Health Medical Home Inc. ensures that every team—clinical, administrative, and operational—works from a single, connected source of truth.
            </p>
            
            {/* Care at Home Model - Service Buttons */}
            <div className="mt-10">
              <h3 className="text-xl font-semibold text-center mb-6 text-orange-400">
                Our Care at Home Model of Care
              </h3>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-3">
                {serviceButtons.map((service, index) => (
                  <Link 
                    key={index} 
                    to={service.href}
                    className="bg-black border-2 border-yellow-500 rounded-xl p-4 text-center hover:bg-gray-900 transition-all transform hover:scale-105 shadow-lg"
                  >
                    <div className="w-10 h-10 border border-yellow-500 rounded-full flex items-center justify-center mx-auto mb-2">
                      <service.icon className="w-5 h-5 text-yellow-500" />
                    </div>
                    <h4 className="font-semibold text-yellow-500 text-xs leading-tight">{service.title}</h4>
                  </Link>
                ))}
              </div>
            </div>
            
            <p className="text-center text-orange-300 font-medium mt-8 text-lg italic">
              It is not just a software; it is the operational backbone of a modern, intelligent, community-centered health system.
            </p>
          </div>
        </div>

        {/* Quick Access Section */}
        <div className="bg-gray-50 py-10 px-6">
          <div className="max-w-7xl mx-auto">
            <h2 className="text-2xl font-bold text-gray-800 mb-6 text-center">
              Quick Access
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {mainLinks.map((link, index) => (
                <Link
                  key={index}
                  to={link.href}
                  className="bg-white p-5 rounded-xl shadow-sm border border-gray-200 hover:shadow-md hover:border-orange-300 transition-all group"
                >
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center mb-3 group-hover:bg-orange-200 transition-colors">
                    <link.icon className="w-5 h-5 text-orange-600" />
                  </div>
                  <h3 className="font-semibold text-gray-800 text-sm group-hover:text-orange-600 transition-colors">
                    {link.name}
                  </h3>
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Contact Info */}
        <div className="bg-white py-6 px-6 border-t border-gray-200">
          <div className="max-w-7xl mx-auto flex flex-wrap justify-center gap-8 text-gray-600 text-sm">
            <div className="flex items-center gap-2">
              <Phone className="w-4 h-4 text-orange-500" />
              <span>(562) 349-0860</span>
            </div>
            <div className="flex items-center gap-2">
              <Mail className="w-4 h-4 text-orange-500" />
              <span>info@ivory_hhc.com</span>
            </div>
            <div className="flex items-center gap-2">
              <Activity className="w-4 h-4 text-orange-500" />
              <span>Providing Quality Home Health Care Services</span>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default QuickLinksPage;
